import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './UserManagement.css';

interface User {
  uid: string;
  email: string;
  displayName: string | null;
  roles: string[];
  createdAt: string;
  lastLogin: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [pendingRoles, setPendingRoles] = useState<{ [uid: string]: string[] }>({});
  const { getIdToken } = useAuth();

  const backendUrl = window.location.hostname === 'localhost' 
    ? 'http://localhost:8080'
    : 'https://voice-coach-backend-406125116283.us-east1.run.app';

  useEffect(() => {
    console.log('🔥 UserManagement useEffect triggered, calling fetchUsers');
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    console.log('🔥 fetchUsers called!');
    try {
      setLoading(true);
      const token = await getIdToken();
      console.log('🔥 Got token:', token ? 'YES' : 'NO');
      if (!token) {
        throw new Error('No authentication token available');
      }

      console.log('🔥 Making API call to:', `${backendUrl}/api/users`);
      const response = await fetch(`${backendUrl}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🔥 API response status:', response.status);
      console.log('🔥 API response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('🔥 API error response:', errorText);
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('👥 UserManagement: Fetched users data:', data);
      console.log('👥 UserManagement: Number of users:', data?.length);
      setUsers(data);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditRoles = (uid: string) => {
    setEditingUser(uid);
    const user = users.find(u => u.uid === uid);
    if (user) {
      setPendingRoles({ ...pendingRoles, [uid]: [...user.roles] });
    }
  };

  const handleRoleToggle = (uid: string, role: string) => {
    const currentRoles = pendingRoles[uid] || [];
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    setPendingRoles({ ...pendingRoles, [uid]: newRoles });
  };

  const handleSaveRoles = async (uid: string) => {
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      console.log('🔧 Updating roles for user:', uid);
      console.log('🔧 New roles:', pendingRoles[uid]);
      console.log('🔧 API URL:', `${backendUrl}/api/users/${uid}/roles`);

      const response = await fetch(`${backendUrl}/api/users/${uid}/roles`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ roles: pendingRoles[uid] })
      });

      console.log('🔧 Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔧 Error response:', errorText);
        throw new Error(`Failed to update roles: ${response.statusText}`);
      }

      // Update local state
      setUsers(users.map(user => 
        user.uid === uid 
          ? { ...user, roles: pendingRoles[uid] }
          : user
      ));
      setEditingUser(null);
    } catch (err: any) {
      setError(err.message);
      console.error('Error updating roles:', err);
    }
  };

  const handleCancelEdit = (uid: string) => {
    setEditingUser(null);
    const { [uid]: _, ...rest } = pendingRoles;
    setPendingRoles(rest);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return <div className="user-management-loading">Loading users...</div>;
  }

  if (error) {
    return (
      <div className="user-management-error">
        <p>Error: {error}</p>
        <button onClick={fetchUsers}>Retry</button>
      </div>
    );
  }

  console.log('👥 UserManagement render - users count:', users.length);
  console.log('👥 UserManagement render - loading:', loading);
  console.log('👥 UserManagement render - error:', error);

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h2>User Management</h2>
        <button onClick={fetchUsers} className="refresh-button">
          Refresh
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Display Name</th>
              <th>First Login</th>
              <th>Last Login</th>
              <th>Roles</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => {
              console.log('👤 Rendering user:', user.email, 'with roles:', user.roles);
              return (
              <tr key={user.uid}>
                <td>{user.email}</td>
                <td>{user.displayName || '-'}</td>
                <td>{formatDate(user.createdAt)}</td>
                <td>{formatDate(user.lastLogin)}</td>
                <td>
                  {editingUser === user.uid ? (
                    <div className="role-checkboxes">
                      <label>
                        <input
                          type="checkbox"
                          checked={pendingRoles[user.uid]?.includes('learner') || false}
                          onChange={() => handleRoleToggle(user.uid, 'learner')}
                        />
                        Learner
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={pendingRoles[user.uid]?.includes('coach') || false}
                          onChange={() => handleRoleToggle(user.uid, 'coach')}
                        />
                        Coach
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={pendingRoles[user.uid]?.includes('admin') || false}
                          onChange={() => handleRoleToggle(user.uid, 'admin')}
                        />
                        Admin
                      </label>
                    </div>
                  ) : (
                    <div className="role-badges">
                      {user.roles.map(role => (
                        <span key={role} className={`role-badge role-${role}`}>
                          {role}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td>
                  {editingUser === user.uid ? (
                    <div className="action-buttons">
                      <button 
                        onClick={() => handleSaveRoles(user.uid)}
                        className="save-button"
                      >
                        Save
                      </button>
                      <button 
                        onClick={() => handleCancelEdit(user.uid)}
                        className="cancel-button"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={() => handleEditRoles(user.uid)}
                      className="edit-button"
                    >
                      Edit Roles
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagement;